// Keeping the board and the server in step. Local edits apply at once and are
// queued; the queue flushes in small batches and says out loud when it cannot.
// The socket brings in what agents, scripts and other tabs change.

import { ensurePush, listenForPushClicks, stopPush } from "./push";
import { getViewer, guestPass, passAge, readShared, renewPass } from "./guest";
import { adoptRunnerTurns, catchUpRunnerTurns, onRunnerEvent, onRunnerState } from "../agent/runnerChat";
import { setMadeThemes, themeDef } from "../themes/themes";
import { withWipe } from "../lib/wipe";
import type { ID, Op, ServerEvent, Snapshot, ThemeSettings } from "../../shared/types";
import { descendants } from "../../shared/entries";
import { isPatch, joinPuts, layEntry } from "./patches";
import { ApiError, api, getToken, setToken } from "../lib/api";
import { boardItems, get, glowItems, loadSnapshot, set, splitBoards, toast, useStore } from "./store";
import { targetOf } from "./place";
import { CLIENT_EPOCH } from "../../shared/epoch";
import type { RenderReport } from "../../shared/widgetKit";
import { forgetEverything, onChatsEvent, readChatList } from "../agent/store";
import { onAgentConnections } from "./agentConnections";
import { DEMO } from "../demo/mode";
import { shownRunLine } from "../../shared/runLine";

/** Changes made on the server (an agent's, a source's), as they arrive. */
const serverOpListeners = new Set<(ops: Op[]) => void>();
export function onServerOps(fn: (ops: Op[]) => void): () => void {
  serverOpListeners.add(fn);
  return () => serverOpListeners.delete(fn);
}

/**
 * A page's changes from elsewhere (an agent, another tab), as they arrive and before a change this tab has
 * waiting for the same page sets them aside: the page editor merges them by block. who: an agent's name.
 */
const entryListeners = new Set<(ops: Op[], from: string | null, who: string | null) => void>();
export function onEntryOps(fn: (ops: Op[], from: string | null, who: string | null) => void): () => void {
  entryListeners.add(fn);
  return () => entryListeners.delete(fn);
}

/** Changes the server said it applied, with its clock when it did: the page editor's last state both share. */
const savedListeners = new Set<(ops: Op[], at: number | null) => void>();
export function onSaved(fn: (ops: Op[], at: number | null) => void): () => void {
  savedListeners.add(fn);
  return () => savedListeners.delete(fn);
}

/** Changes the server refused, each with its words: an import writing in batches stops at the first (src/import). */
const refusedListeners = new Set<(refused: { op: Op; error: string }[]) => void>();
export function onRefused(fn: (refused: { op: Op; error: string }[]) => void): () => void {
  refusedListeners.add(fn);
  return () => refusedListeners.delete(fn);
}

/** Names whose activity should not pop up a notification, e.g. Claude while its chat is open. */
export const quietToasts = new Set<string>();

let queue: Op[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let inflight = false;
let backoff = 0;

function keyOf(op: Op): string {
  switch (op.op) {
    case "item.put":
      return "i:" + op.item.id;
    case "item.del":
      return "i:" + op.id;
    case "board.put":
      return "b:" + op.board.id;
    case "board.del":
      return "b:" + op.id;
    case "entry.put":
      return "e:" + op.entry.id;
    case "entry.del":
      return "e:" + op.id;
    case "workspace.put":
      return "w";
  }
}

export function pendingIds(): Set<string> {
  return new Set(queue.map(keyOf));
}

/**
 * A sheet's band changed twice before the first change went: the cells of both (shared/sheet/model.ts). A patch over
 * a whole band waiting to go is that band with the patch laid over it; a whole band replaces what waited.
 */
function coalesce(older: Op, newer: Op): Op {
  if (older.op !== "entry.put" || newer.op !== "entry.put" || !isPatch(newer.entry)) return newer;
  // a slide's patch too, by its fields and boxes (S5)
  return { op: "entry.put", entry: joinPuts(older.entry, newer.entry) };
}

/** A band from the server with this tab's changes to it that have not reached the server yet laid over it. */
function withPending(op: Extract<Op, { op: "entry.put" }>): Extract<Op, { op: "entry.put" }> {
  let entry = op.entry;
  for (const o of [...(sending?.ops ?? []), ...queue]) {
    if (o.op !== "entry.put" || o.entry.id !== op.entry.id) continue;
    entry = { ...op.entry, data: layEntry(entry, o.entry).data };
  }
  return { op: "entry.put", entry };
}

export function enqueue(ops: Op[]) {
  // nothing to send (an undo with nothing left to take back) leaves the save as it was
  if (!ops.length) return;
  // the demo keeps every change in this tab: nothing is sent, and what waits on a save hears it landed, as from the board
  if (DEMO) {
    setTimeout(() => {
      for (const fn of savedListeners) fn(ops, Date.now());
    }, 30);
    return;
  }
  for (const op0 of ops) {
    const k = keyOf(op0);
    // a sheet's band changed again before the last change went: both changes' cells go
    const waiting = op0.op === "entry.put" && isPatch(op0.entry) ? queue.find((q) => keyOf(q) === k) : undefined;
    const op = waiting ? coalesce(waiting, op0) : op0;
    // the workspace's changes are parts of it (its name, its folders): one waiting takes the newer parts over its own
    const was = op.op === "workspace.put" ? queue.find((q): q is Extract<Op, { op: "workspace.put" }> => q.op === "workspace.put") : undefined;
    queue = queue.filter((q) => keyOf(q) !== k);
    // two waiting changes to the workspace go as one; what was brought back adds up (Round 6)
    const removed = [...(was && op.op === "workspace.put" ? (was.patch.dismissedRemove ?? []) : []), ...(op.op === "workspace.put" ? (op.patch.dismissedRemove ?? []) : [])];
    queue.push(was && op.op === "workspace.put" ? { op: "workspace.put", patch: { ...was.patch, ...op.patch, ...(removed.length ? { dismissedRemove: [...new Set(removed)] } : {}) } } : op);
  }
  set({ save: "saving" });
  schedule(280);
}

function schedule(ms: number) {
  if (billingStopped) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(flush, ms);
}

/**
 * A batch sent to the server: its ops, when the server says it applied them (its own clock, as a snapshot
 * says when it was read), and whether it never got there. The server never sends a tab its own changes
 * back, so a snapshot read before a batch landed lacks it for good: a resync lays such a batch over it.
 */
interface Sent {
  ops: Op[];
  at: number | null;
  failed: boolean;
}
/** The batch on its way now, and the resyncs waiting for a snapshot, each told of every batch sent meanwhile. */
let sending: Sent | null = null;
const resyncing = new Set<Sent[]>();

/**
 * How much one request carries at most, as JSON: the board reads up to 4 MB a request, and what it applied goes on to
 * every other tab as one message, so a batch of long pages (an import, or its redo) goes in several (S3, 25 Oct).
 */
const BATCH_BYTES = 800_000;

/** The next batch from the front of the queue: up to 200 changes, and no more than BATCH_BYTES of them, one at least. */
function nextBatch(): Op[] {
  let bytes = 0;
  let n = 0;
  while (n < queue.length && n < 200) {
    const size = JSON.stringify(queue[n]).length;
    if (n > 0 && bytes + size > BATCH_BYTES) break;
    bytes += size;
    n++;
  }
  return queue.slice(0, n);
}

/** A board's versions of its instructions are the board's own (Features 4): the server never takes a put's copy, so
 * none is sent. */
const withoutVersions = (op: Op): Op => (op.op === "board.put" && op.board.instructionsVersions ? { ...op, board: { ...op.board, instructionsVersions: undefined } } : op);

export async function flush(keepalive = false): Promise<void> {
  if (billingStopped || DEMO) return;
  if (inflight || !queue.length) return;
  const batch = nextBatch();
  queue = queue.slice(batch.length);
  inflight = true;
  const sent: Sent = { ops: batch, at: null, failed: false };
  sending = sent;
  for (const r of resyncing) r.push(sent);
  try {
    // v: this page's epoch, so the board knows it can see coloured words and who wrote each block (shared/epoch.ts)
    // a board shared with this person goes back with its pass (state/guest.ts)
    const guest = get().guest ? guestPass() : null;
    const r = await api<{ ok: boolean; rejected: { op: Op; error: string }[]; serverTime?: number }>(guest ? "/api/guest/ops" : "/api/ops", { body: { ops: batch.map(withoutVersions), client: get().clientId, v: CLIENT_EPOCH }, keepalive, ...(guest ? { token: guest } : {}) });
    backoff = 0;
    // what the server refused is its to say, not this tab's: no resync lays it over a snapshot
    const refused = new Set((r.rejected ?? []).map((x) => keyOf(x.op)));
    sent.ops = batch.filter((op) => !refused.has(keyOf(op)));
    sent.at = typeof r.serverTime === "number" ? r.serverTime : null;
    if (sent.ops.length) for (const fn of savedListeners) fn(sent.ops, sent.at);
    if (r.rejected?.length) for (const fn of refusedListeners) fn(r.rejected);
    if (r.rejected?.length) {
      // a board archived on another device a moment before: said plainly, as what happened rather than a fault
      if (r.rejected.every((x) => /is archived, so it is read only/.test(x.error))) {
        set({ save: queue.length ? "saving" : "saved", saveError: null });
        toast({ level: "info", title: "That board was archived", body: "So the last change to it was not saved. Restore it to change it.", key: "archived" });
      } else if (r.rejected.some((x) => /This tab is out of date/.test(x.error))) {
        // a page from before the board's newest release: one notice with its way out, however many saves it refuses,
        // and never a reload by itself, which would lose what it could not save (Round 6)
        staleTab = true;
        refusedHere = true;
        set({ save: "error", saveError: r.rejected[0].error });
        toast({ level: "error", title: "Reload this tab to keep editing", body: "Context Board was updated, and this tab is from before it, so its last change was not saved. Reload, then make the change again.", key: "epoch", sticky: true, action: { label: "Reload", run: () => location.reload() } });
      } else {
        set({ save: "error", saveError: r.rejected[0].error });
        // one at a time: a second refusal takes the first one's place, never piling up (Round 6)
        toast({ level: "error", title: "Some changes were not saved", body: r.rejected[0].error, key: "save-error" });
      }
      // the server is the truth: take its copy of anything it refused
      await resync();
    } else set({ save: queue.length ? "saving" : "saved", saveError: null });
  } catch (e) {
    // put the batch back in front, unless newer edits to the same things arrived; a resync takes it from there
    sent.failed = true;
    const newer = new Set(queue.map(keyOf));
    // a sheet's band changed again meanwhile: the change that failed goes with the newer one, never lost to it
    queue = queue.map((q) => {
      const old = batch.find((b) => keyOf(b) === keyOf(q));
      return old && q.op === "entry.put" && isPatch(q.entry) ? coalesce(old, q) : q;
    });
    queue = [...batch.filter((op) => !newer.has(keyOf(op))), ...queue];
    const err = e as ApiError;
    // a shared board's pass ran out (12 hours): a new one, and the changes go again (review, 20 Oct)
    if (err.status === 401 && get().guest) {
      const renewed = await renewPass().catch(() => false);
      if (renewed) {
        schedule(200);
        return;
      }
    }
    if (err.status === 401) {
      set({ save: "error", saveError: "Your session ended. Sign in again." });
      return;
    }
    set({ save: err.status === 0 ? "offline" : "error", saveError: err.message });
    backoff = Math.min(30_000, backoff ? backoff * 2 : 1500);
    schedule(backoff);
  } finally {
    inflight = false;
    if (sending === sent) sending = null;
    if (queue.length && !timer) schedule(200);
    else if (queue.length) schedule(backoff || 200);
  }
}

/**
 * Leaving the page: what waits goes now, beside a batch still on its way if there is one, which flush alone leaves for
 * later and so loses. What changes as the page goes calls it after (Fixes 12: a new post left empty, thrown away on a reload).
 */
export function flushLeaving() {
  if (!inflight) return void flush(true);
  if (billingStopped || DEMO || !queue.length) return;
  const batch = queue.splice(0);
  const guest = get().guest ? guestPass() : null;
  void api(guest ? "/api/guest/ops" : "/api/ops", { body: { ops: batch.map(withoutVersions), client: get().clientId, v: CLIENT_EPOCH }, keepalive: true, ...(guest ? { token: guest } : {}) }).catch(() => undefined);
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushLeaving);
  window.addEventListener("online", () => {
    if (queue.length) schedule(100);
    // back on the network: the board read again, so an open page takes what changed while it was off (Round 6)
    if (getToken()) void resync();
  });
  window.addEventListener("beforeunload", (e) => {
    if (queue.length) {
      void flush(true);
      e.preventDefault();
    }
  });
}

// ---------------------------------------------------------------------------
// applying ops to local state

export function applyOps(ops: Op[]) {
  set((s) => {
    const items = { ...s.items };
    let entries = s.entries;
    let boards = s.boards;
    let archived = s.archived;
    let selection = s.selection;
    let boardId = s.boardId;
    let workspace = s.workspace;
    for (const op of ops) {
      // the workspace's own (its name, the boards list's folders), here at once as on the server
      if (op.op === "workspace.put" && workspace) workspace = { ...workspace, ...op.patch };
      if (op.op === "item.put") items[op.item.id] = op.item;
      else if (op.op === "item.del") {
        delete items[op.id];
        selection = selection.filter((x) => x !== op.id);
      } else if (op.op === "board.put") {
        // in use or archived, it is in one list or the other
        ({ boards, archived } = splitBoards([...boards, ...archived].filter((b) => b.id !== op.board.id).concat(op.board)));
      } else if (op.op === "board.del") {
        boards = boards.filter((b) => b.id !== op.id);
        archived = archived.filter((b) => b.id !== op.id);
        for (const id in items) if (items[id].boardId === op.id) delete items[id];
        if (Object.values(entries).some((e) => e.boardId === op.id)) entries = Object.fromEntries(Object.entries(entries).filter(([, e]) => e.boardId !== op.id));
        if (boardId === op.id) boardId = boards[0]?.id ?? null;
      } else if (op.op === "entry.put") {
        // a sheet's band changed by its cells alone: laid over the band here
        const was = entries[op.entry.id];
        entries = { ...entries, [op.entry.id]: layEntry(was, op.entry) };
      } else if (op.op === "entry.del" && entries[op.id]) {
        // the entries inside it go with it, as they do on the server
        const gone = new Set([op.id, ...descendants(Object.values(entries), op.id).map((e) => e.id)]);
        entries = Object.fromEntries(Object.entries(entries).filter(([id]) => !gone.has(id)));
      }
    }
    return { items, entries, boards, archived, selection, boardId, workspace };
  });
}

// ---------------------------------------------------------------------------
// the socket

let ws: WebSocket | null = null;
let wsTries = 0;
let pingTimer: ReturnType<typeof setInterval> | null = null;
/** When the link last heard anything from the board, its answer to a ping included, and when it last pinged. */
let heardAt = 0;
let pingedAt = 0;
/** A ping the board has not answered in ten seconds: the link is dead, however open it looks. */
const deaf = () => pingedAt > heardAt && Date.now() - pingedAt > 10_000;

/** A link that went deaf: let go of it without waiting on its close, and open a new one, which reads the board again. */
function dropDeadSocket() {
  const dead = ws;
  if (!dead) return;
  ws = null;
  dead.onclose = null;
  dead.onmessage = null;
  try {
    dead.close();
  } catch {
    /* gone already */
  }
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = null;
  wasOffline = true;
  set({ live: "offline" });
  connectSocket();
}
let wasOffline = false;
/** A page from before the board's newest release (shared/epoch.ts): back after a while with nothing unsaved, it loads the new one. */
let staleTab = false;
/** A change this tab made was refused: it never reloads by itself, so what it could not save stays on screen. */
let refusedHere = false;
let billingStopped = false;

/** A locked board has no live widgets, socket or retries. Keep unsent edits in memory. */
export function stopForBilling() {
  billingStopped = true;
  if (timer) { clearTimeout(timer); timer = null; }
  if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
  if (ws) { ws.onclose = null; ws.close(); ws = null; }
}

export function connectSocket() {
  if (billingStopped || DEMO) return;
  const guest = !!get().guest;
  // a shared board's pass close to running out is renewed before its socket goes again
  if (guest && passAge() > 11 * 3600_000) {
    void renewPass().then((ok) => ok && connectSocket());
    return;
  }
  const token = guest ? guestPass() : getToken();
  if (!token) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}${guest ? "/api/guest/ws" : "/api/ws"}?token=${encodeURIComponent(token)}&client=${get().clientId}`);
  set({ live: "connecting" });
  ws.onopen = () => {
    wsTries = 0;
    set({ live: "live" });
    if (wasOffline) void resync();
    wasOffline = false;
    if (pingTimer) clearInterval(pingTimer);
    // a ping the board leaves unanswered means the link is dead though it looks open (a computer that slept, a network
    // that changed): it goes, and the next one reads the board again. Judged by the unanswered ping, never by time
    // alone, since a hidden tab's timer may run only once a minute (Round 6)
    heardAt = Date.now();
    pingedAt = 0;
    pingTimer = setInterval(() => {
      if (ws?.readyState !== WebSocket.OPEN) return;
      if (deaf()) return dropDeadSocket();
      pingedAt = Date.now();
      ws.send("ping");
    }, 25_000);
    // the server tells an agent whether anyone is looking at the board it changed, and whether only on a phone
    sendView();
    sentRender.clear();
  };
  ws.onmessage = (e) => {
    heardAt = Date.now();
    if (e.data === "pong") return;
    try {
      handleEvent(JSON.parse(e.data) as ServerEvent);
    } catch (err) {
      console.warn("bad event", err);
    }
  };
  ws.onclose = (e) => {
    if (pingTimer) clearInterval(pingTimer);
    ws = null;
    wasOffline = true;
    set({ live: "offline" });
    // 4000: the board is open in too many tabs and this one was asked to step aside
    if (e.code === 4401 || e.code === 1008 || e.code === 4000) return;
    // a shared board: taken off it, moved to view only, or the board gone; a new pass says which (state/guest.ts)
    if (guest && (e.code === 4403 || e.code === 4402)) {
      void import("./guest").then((g) => g.reloadShared());
      return;
    }
    if (e.code === 4402 || e.code === 4403) {
      window.dispatchEvent(new Event("cb:billing-required"));
      return;
    }
    const delay = Math.min(30_000, 800 * 2 ** wsTries++);
    setTimeout(connectSocket, delay);
  };
  ws.onerror = () => {
    /* onclose follows */
  };
}

function sendSocket(msg: Record<string, unknown>) {
  if (ws?.readyState !== WebSocket.OPEN) return;
  try {
    ws.send(JSON.stringify(msg));
  } catch {
    /* closing */
  }
}

function zone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** When this tab last went to the board it shows. Saying again which board it shows (narrower, wider, or back after a dropped connection) is no switch. */
let switchedAt = Date.now();

/**
 * Which board this tab shows, whether in the narrow layout's column, where
 * widgets draw at another size than their tiles, and how long ago it went to
 * that board, so the server can tell which board the user went to last.
 */
/** On a Files board, the note open in this tab: its path, for the board's agents to know which note "this note" is. */
let viewNote: string | null = null;
export function setViewNote(path: string | null) {
  if (path === viewNote) return;
  viewNote = path;
  sendView();
}

/** What is open in this tab, by kind and id (openNow.ts): said when it changes, so read_board can tell an outside agent. */
export interface ViewOpen {
  kind: "page" | "post" | "deal" | "idea" | "slide" | "widget";
  id: string;
}
let viewOpen: ViewOpen | null = null;
let openTimer: ReturnType<typeof setTimeout> | null = null;
export function setViewOpen(open: ViewOpen | null) {
  if (open?.kind === viewOpen?.kind && open?.id === viewOpen?.id) return;
  viewOpen = open;
  // a run of clicks says the last of them once
  if (openTimer) clearTimeout(openTimer);
  openTimer = setTimeout(() => sendView(), 250);
}

function sendView() {
  // pictures: this tab can take a picture of a custom widget's drawing when an agent asks for one
  // tz: the user's own time zone, so agents' Calendar tools know which day and hour it is where they are
  // seen: the tab is in front of the user now, so what an agent shows them (show) comes here, not to a tab left behind
  const seen = typeof document !== "undefined" && document.visibilityState === "visible" && (typeof document.hasFocus !== "function" || document.hasFocus());
  // hidden: behind another tab or minimised, so its board's sources rest until it is back (a window that is only
  // unfocused, a board on a second screen, still counts as in view)
  const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
  sendSocket({ type: "view", board: get().boardId, phone: get().mobile, ago: Math.max(0, Date.now() - switchedAt), pictures: true, tz: zone(), seen, ...(hidden ? { hidden: true } : {}), ...(viewNote ? { note: viewNote } : {}), open: viewOpen });
}

// a tab brought to the front says so, and one sent behind another says that too
if (typeof window !== "undefined" && typeof window.addEventListener === "function" && typeof document !== "undefined" && typeof document.addEventListener === "function") {
  window.addEventListener("focus", () => sendView());
  document.addEventListener("visibilitychange", () => sendView());
}

useStore.subscribe((s, prev) => {
  if (s.boardId !== prev.boardId) switchedAt = Date.now();
  if (s.boardId !== prev.boardId || s.mobile !== prev.mobile) sendView();
});

const sentRender = new Map<ID, string>();

/**
 * Tells the server how a custom widget drew in its own tile on the board, so
 * the agent that wrote it hears back. size: that tile's size, so an agent that
 * resized it waits for a drawing at the new size. The stream it reads goes
 * too: a drawing from other data is another drawing, even one that looks the
 * same, so an agent that changed its stream hears how it draws from the new one.
 */
/** A picture of a custom widget's first drawing in new code, sent just before its report, for the agent that wrote it to see. */
/** A picture of a custom widget's drawing in its tile (its code, size and stream), or "" when this browser could not take one. */
export function reportShot(id: ID, hash: string, png: string, size: { w: number; h: number }, stream: string | null) {
  if (png.length <= 600_000) sendSocket({ type: "shot", id, hash, size, stream, png });
}

export function reportRender(id: ID, hash: string, report: RenderReport, size: { w: number; h: number }) {
  const it = get().items[id];
  const stream = it?.kind === "widget" ? (it.stream ?? null) : null;
  const key = hash + JSON.stringify(report) + JSON.stringify(size) + JSON.stringify(stream);
  if (sentRender.get(id) === key) return;
  sentRender.set(id, key);
  sendSocket({ type: "render", id, hash, report, size, stream });
}

/** When this tab was last hidden, to tell a glance away from a return after a while. */
let hiddenAt = 0;
/**
 * Back to a tab left a while (half a minute or more): it reads the board again, whether or not its link still looks
 * open, since a computer that slept can keep a link that hears nothing. An open page takes what changed meanwhile.
 * A tab from before a release, with nothing waiting to save and nothing refused, loads the new one (Round 6, 21 Oct).
 */
document.addEventListener?.("visibilitychange", () => {
  if (document.visibilityState !== "visible") {
    hiddenAt = Date.now();
    return;
  }
  if (!ws) connectSocket();
  // a phone that locked heard nothing meanwhile, even with its link still open: the chat asks what it missed
  catchUpRunnerTurns();
  const away = hiddenAt && Date.now() - hiddenAt > 30_000;
  hiddenAt = 0;
  if (!away || !getToken()) return;
  if (staleTab && reloadIfIdle()) return;
  // a link left with a ping unanswered is dead: a new one, which reads the board as it opens
  if (ws && deaf()) return dropDeadSocket();
  void resync();
});

/** A tab from before the board's newest release loads the new one, when nothing waits to save; at most once in ten minutes. */
function reloadIfIdle(): boolean {
  if (queue.length || inflight || refusedHere || get().save !== "saved") return false;
  try {
    const last = Number(sessionStorage.getItem("cb:epoch-reload") ?? 0);
    if (Date.now() - last < 600_000) return false;
    sessionStorage.setItem("cb:epoch-reload", String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}

function handleEvent(ev: ServerEvent) {
  const s = get();
  switch (ev.type) {
    case "hello":
      // a deploy restarts the board, so every tab says hello again: with tools changed, the chat lists them again
      set({ serverOffset: ev.serverTime - Date.now(), ...(typeof ev.tools === "string" ? { toolsSig: ev.tools } : {}) });
      // the board went on to an epoch this page is older than: it asks once to reload (shared/epoch.ts)
      if (typeof ev.epoch === "number" && ev.epoch > CLIENT_EPOCH) staleTab = true;
      if (typeof ev.epoch === "number" && ev.epoch > CLIENT_EPOCH)
        toast({ level: "info", title: "Context Board was updated", body: "Reload this tab to keep working with the newest version. Your board is saved.", key: "epoch", sticky: true, action: { label: "Reload", run: () => location.reload() } });
      break;
    case "ops": {
      if (ev.from === null) for (const fn of serverOpListeners) fn(ev.ops);
      if (entryListeners.size && ev.ops.some((op) => op.op === "entry.put")) for (const fn of entryListeners) fn(ev.ops, ev.from, ev.who ?? null);
      const pending = pendingIds();
      // a sheet's band this tab has changes to on their way: the server's band with those changes laid over it, so
      // the user's cells and an agent's both stay (shared/sheet/model.ts)
      const going = new Set([...pending, ...(sending?.ops ?? []).map(keyOf)]);
      const ops = ev.ops
        .map((op) => (op.op === "entry.put" && (op.entry.t === "cells" || op.entry.t === "slide") && going.has(keyOf(op)) ? withPending(op) : op))
        .filter((op) => {
          // a slide too (S5): another writer's boxes come in under this tab's own, by id
          return !pending.has(keyOf(op)) || (op.op === "entry.put" && (op.entry.t === "cells" || op.entry.t === "slide"));
        });
      // an agent changing a board's look: it plays as a wipe, and can be taken back
      const looks = ev.from === null ? ops.filter((op): op is Extract<Op, { op: "board.put" }> => op.op === "board.put").map((op) => ({ op, before: get().boards.find((b) => b.id === op.board.id)?.theme })).filter((x) => x.before && (x.before.custom ?? null) !== (x.op.board.theme.custom ?? null) || (x.before && x.before.id !== x.op.board.theme.id)) : [];
      // an item someone is dragging right now keeps its local position
      if (looks.some((x) => x.op.board.id === get().boardId)) withWipe(() => applyOps(ops));
      else applyOps(ops);
      for (const { op, before } of looks) {
        if (op.board.id !== get().boardId || !before) continue;
        const name = themeDef(op.board.theme.custom ?? op.board.theme.id)?.name ?? "a new theme";
        const key = "look:" + op.board.id;
        // a second change replaces the first toast, and its undo still goes back to where the user started
        const earlier = get().toasts.find((x) => x.key === key)?.meta as ThemeSettings | undefined;
        const start = earlier ?? before;
        toast({
          level: "info",
          who: ev.who ?? undefined,
          title: ev.who ? `Changed this board's theme to “${name}”` : `Now using “${name}”`,
          action: { label: "Undo", run: () => void import("./actions").then((m) => m.updateTheme(start)) },
          key,
          meta: start,
          ms: 9000,
        });
      }
      const glow: ID[] = [];
      for (const op of ops) if (op.op === "item.put") glow.push(op.item.id);
      // a page an agent or another tab changed glows once too, where it shows (its row in the page tree)
      else if (op.op === "entry.put") glow.push(op.entry.id);
      glowItems(glow);
      break;
    }
    case "shoot":
      // to the widget, if this tab shows it in its tile
      window.dispatchEvent(new CustomEvent("cb:shoot", { detail: ev.id }));
      break;
    case "stream": {
      const old = s.streams[ev.stream.key];
      set((st) => ({ streams: { ...st.streams, [ev.stream.key]: ev.stream } }));
      if (!old || old.updatedAt !== ev.stream.updatedAt || old.error !== ev.stream.error) {
        const ids = Object.values(get().items)
          .filter((i) => i.kind === "widget" && i.stream === ev.stream.key)
          .map((i) => i.id);
        glowItems(ids);
      }
      break;
    }
    case "stream.del":
      set((st) => {
        const streams = { ...st.streams };
        delete streams[ev.key];
        return { streams };
      });
      break;
    case "pull":
      set((st) => ({ pulls: { ...st.pulls, [ev.pull.id]: ev.pull } }));
      break;
    case "pull.del":
      set((st) => {
        const pulls = { ...st.pulls };
        delete pulls[ev.id];
        return { pulls };
      });
      break;
    case "connection":
      set((st) => ({ connections: { ...st.connections, [ev.connection.id]: ev.connection } }));
      break;
    case "service":
      set((st) => ({ services: { ...st.services, [ev.service.id]: ev.service } }));
      break;
    case "service.del":
      set((st) => {
        const services = { ...st.services };
        delete services[ev.id];
        return { services };
      });
      break;
    case "theme": {
      const themes = { ...get().themes, [ev.theme.id]: ev.theme };
      setMadeThemes(Object.values(themes));
      set({ themes });
      break;
    }
    case "gcal":
      // a Google calendar's events changed (worker/gcal.ts): a Calendar board showing it reads them again
      void import("./gcal").then((m) => m.gcalChanged());
      break;
    case "arranged": {
      // a whole board moved at once: whoever is looking at it sees it, once the moves have drawn; full screen keeps its own fit
      if (ev.boardId !== get().boardId) break;
      setTimeout(() => {
        const s = get();
        if (s.boardId !== ev.boardId) return;
        if (s.presenting) void import("../chrome/Fullscreen").then(({ fitForScreen }) => fitForScreen());
        else
          void import("../canvas/camera").then(({ camera }) =>
            import("../canvas/geometry").then(({ bounds, itemRect }) => camera.fitSmart(bounds(boardItems(s).map(itemRect)))),
          );
      }, 120);
      break;
    }
    // a deck theme the workspace saved or deleted (S6)
    case "deckTheme":
    case "deckTheme.del":
      void import("../boards/slides/theme/saved").then((m) => m.onDeckThemeEvent(ev));
      break;
    case "theme.del": {
      const themes = { ...get().themes };
      delete themes[ev.id];
      setMadeThemes(Object.values(themes));
      set({ themes });
      break;
    }
    case "connection.del":
      set((st) => {
        const connections = { ...st.connections };
        delete connections[ev.id];
        return { connections };
      });
      break;
    case "activity": {
      const a = shownRunLine(ev.activity);
      set((st) => ({ activity: [a, ...st.activity.filter((x) => x.id !== a.id)].slice(0, 200) }));
      // on a plan open in front of the user its thread already shows what happened, so nothing pops up over it
      const onOpenPlan = !!a.boardId && a.boardId === get().boardId && get().boards.some((b) => b.id === a.boardId && b.kind === "plan");
      if (a.who !== "You" && a.kind !== "system" && !quietToasts.has(a.who) && !onOpenPlan) {
        const itemId = a.itemId;
        // an agent's change to a board's instructions can be undone from its pop-up too (Features 4)
        const instr = a.instr;
        // and a board an agent deleted comes back whole from its pop-up (Features 5)
        const gone = a.deleted?.board;
        // and an agent's change to a sheet that wrote over or cleared cells is taken back from its pop-up (Fixes 9)
        const sheet = a.sheetUndo?.trash;
        toast({ level: a.level, title: a.title, body: a.body, who: a.who, via: a.via ?? null, itemId, to: targetOf(a), ...(instr ? { action: { label: "Undo", run: () => void import("../chrome/InstrChange").then((m) => m.undoChange(instr)) } } : gone ? { action: { label: "Undo", run: () => void import("./actions").then((m) => m.undeleteBoard(gone)) } } : sheet ? { action: { label: "Undo", run: () => void import("../agent/SheetUndo").then((m) => m.undoSheetChange(sheet)) } } : {}) });
      }
      break;
    }
    case "agent":
      set((st) => ({ agents: { ...st.agents, [ev.agent.name]: ev.agent } }));
      glowItems(
        Object.values(get().items)
          .filter((i) => i.kind === "widget" && i.type === "agent" && (String(i.props.agent ?? "").toLowerCase() === ev.agent.name.toLowerCase() || !i.props.agent))
          .map((i) => i.id),
      );
      break;
    case "keys":
      set({ keys: ev.keys });
      break;
    case "runner":
      set({ runner: ev.runner });
      onRunnerState(ev.runner);
      break;
    // a computer's agent servers, read again or being read, for the Connections widget
    case "agent-connections":
      onAgentConnections(ev.computer);
      break;
    // a project file a widget shows, as the user's computer sent it
    case "file":
      set((st) => ({ files: { ...st.files, [ev.file.path]: ev.file } }));
      break;
    // what changed in the project folders, for a Files board open in this tab: passed on, never kept
    case "files":
      window.dispatchEvent(new CustomEvent("cb:files", { detail: ev }));
      break;
    // the folder picker on the user's computer closed: the settings that asked for it hear what was chosen
    case "picked":
      window.dispatchEvent(new CustomEvent("cb:picked", { detail: ev }));
      break;
    case "chat":
      onRunnerEvent(ev.turn, ev.event, ev.seq);
      // a turn that ended is no longer one to follow
      if (ev.event.kind === "done" || ev.event.kind === "error") set((s) => ({ runnerTurns: s.runnerTurns.filter((t) => t.turn !== ev.turn) }));
      break;
    // a chat saved, renamed, pinned, read or deleted, here or on another device: the Chats list follows
    case "chats":
      onChatsEvent(ev);
      break;
    // a brief or a job began or ended on the user's computer: its widget says it is running while it is
    case "jobs":
      set({ jobsRunning: ev.running });
      break;
    // a Button's run began, waits on the user, goes on or ended, pressed here or on another device (Round 7)
    case "button":
      set((s) => ({ buttonRuns: { ...s.buttonRuns, [ev.item]: ev.run } }));
      break;
    // the bell's list, and what was just raised to pop up (Features 1, 20 Oct)
    case "notices":
      void import("./notices").then((m) => m.onNotices(ev));
      break;
    case "workspace":
      set({ workspace: { ...ev.workspace, lastVisitAt: get().workspace?.lastVisitAt ?? null } });
      break;
    case "reload":
      void resync();
      break;
    case "navigate":
      // an agent showing the user what they asked to see; loaded when first asked, so sync stays clear of the screens
      void import("./navigate").then((m) => m.navigateTo(ev));
      break;
  }
}

/** The server's copy, with the changes still waiting to be sent made to its items and boards. */
function withQueued(snap: Snapshot, ops: Op[]): Snapshot {
  if (!ops.length) return snap;
  const items = new Map(snap.items.map((i) => [i.id, i]));
  const entries = new Map((snap.entries ?? []).map((e) => [e.id, e]));
  const boards = new Map(snap.boards.map((b) => [b.id, b]));
  let workspace = snap.workspace;
  for (const op of ops) {
    if (op.op === "item.put") items.set(op.item.id, op.item);
    else if (op.op === "item.del") items.delete(op.id);
    else if (op.op === "board.put") boards.set(op.board.id, op.board);
    else if (op.op === "board.del") {
      boards.delete(op.id);
      for (const [id, i] of items) if (i.boardId === op.id) items.delete(id);
      for (const [id, e] of entries) if (e.boardId === op.id) entries.delete(id);
    } else if (op.op === "entry.put") {
      // a change to a sheet's band is its cells alone, laid over the band the snapshot has
      const was = entries.get(op.entry.id);
      entries.set(op.entry.id, layEntry(was, op.entry));
    } else if (op.op === "entry.del") for (const e of [{ id: op.id }, ...descendants([...entries.values()], op.id)]) entries.delete(e.id);
    else if (op.op === "workspace.put" && workspace) workspace = { ...workspace, ...op.patch };
  }
  return { ...snap, workspace, items: [...items.values()], entries: [...entries.values()], boards: [...boards.values()] };
}

export async function resync() {
  if (DEMO) return;
  if (get().guest) {
    // a shared board comes again as its owner's account gives it
    await (await import("./guest")).reloadShared();
    return;
  }
  // every batch on its way, or sent before the snapshot is back: the snapshot may have been read before it landed
  const sent: Sent[] = sending ? [sending] : [];
  resyncing.add(sent);
  try {
    const snap = await api<Snapshot>("/api/snapshot");
    // what the server had not applied when it read the snapshot (or has not said yet), and anything still
    // waiting to be sent, go in with it, in one step: a change made here is never lost or turned back by a
    // reload, and a widget made here and not confirmed yet is never missing for a moment in between, so
    // nothing showing it (its code editor) lets go. A batch applied before the snapshot was read is in it,
    // with whatever came after it from elsewhere.
    const unseen = sent.filter((b) => !b.failed && (b.at === null || b.at >= snap.serverTime)).flatMap((b) => b.ops);
    // an open page merges the board's copy with what was typed and not saved, as it does a change that arrives live
    loadSnapshot(withQueued(snap, [...unseen, ...queue]));
    adoptRunnerTurns();
    // back after a dropped link: a turn this page followed asks for what it said meanwhile, and the chats list what changed
    catchUpRunnerTurns();
    void readChatList();
  } catch (e) {
    console.warn("resync failed", e);
  } finally {
    resyncing.delete(sent);
  }
}

/** The demo's scripted agent (src/demo/player.ts): what the board sent while the real tools ran, played as it came. */
export function demoEvent(ev: ServerEvent) {
  if (DEMO) handleEvent(ev);
}

// ---------------------------------------------------------------------------
// boot

export async function boot() {
  const token = getToken();
  if (!token) {
    // signed in only to see boards shared with them: their list (Features 1, sharing)
    set({ phase: getViewer() ? "shared" : "welcome" });
    return;
  }
  try {
    if (typeof location !== "undefined" && new URLSearchParams(location.search).get("checkout") === "success") await api("/api/billing");
    const snap = await api<Snapshot>("/api/snapshot");
    if (token !== getToken()) return;
    const recovering = billingStopped && get().workspace?.id === snap.workspace.id;
    loadSnapshot(recovering ? withQueued(snap, queue) : snap, recovering);
    billingStopped = false;
    adoptRunnerTurns();
    set({ phase: "ready" });
    connectSocket();
    if (queue.length) schedule(0);
    // notifications with the board closed, where they are allowed, and one clicked to get here (Features 1, 20 Oct)
    listenForPushClicks();
    void ensurePush();
    // boards others shared with this person, for the boards list (Features 1, sharing)
    void readShared()
      .then((shared) => set({ shared }))
      .catch(() => null);
  } catch (e) {
    const err = e as ApiError;
    if (err.status === 401 || err.status === 404) {
      void stopPush(false);
      setToken(null);
      set({ phase: "welcome" });
    } else set({ phase: "error", bootError: err.message, bootErrorCode: err.code });
  }
}

/** Starts a board. `ticket` is a new email's proof of sign-in; only the dev server starts one without it. */
export async function startWorkspace(template: string, ticket?: string, start?: string) {
  // start: the answer to the first question (Features 4), whose board is made in this browser's time zone
  const tz = start ? Intl.DateTimeFormat().resolvedOptions().timeZone || undefined : undefined;
  const r = await api<{ token: string }>("/api/workspaces", { body: { template, ticket, ...(start ? { start, tz } : {}) }, token: null });
  setToken(r.token);
  const snap = await api<Snapshot>("/api/snapshot");
  loadSnapshot(snap, false);
  adoptRunnerTurns();
  set({ phase: "ready" });
  connectSocket();
}

export async function signOut() {
  await flush();
  // this browser stops getting the board's notifications
  await stopPush(true);
  try {
    await api("/api/logout", { body: {} });
  } catch {
    /* signing out locally is what matters */
  }
  setToken(null);
  // the chat and the Anthropic key leave with the session
  forgetEverything();
  ws?.close();
  location.href = "/";
}
